import type { ReactElement } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import { ouvert } from "./game/escalier";
import { interrupteurs } from "./game/menage";
import { capacite } from "./game/moteur";
import { Arbre } from "./screens/Arbre/Arbre";
import { Atelier } from "./screens/Atelier/Atelier";
import { Bibliotheque } from "./screens/Bibliotheque/Bibliotheque";
import { Boutique } from "./screens/Boutique/Boutique";
import { Studio } from "./screens/Chambre/Studio";
import { Fil } from "./screens/Fil/Fil";
import { Fin } from "./screens/Fins/Fin";
import { Guide } from "./screens/Guide/Guide";
import { Historique } from "./screens/Historique/Historique";
import { Victoire } from "./screens/Fins/Victoire";
import { Equipe } from "./screens/Equipe/Equipe";
import { Jalons } from "./screens/Jalons/Jalons";
import { Journal } from "./screens/Journal/Journal";
import { Logement } from "./screens/Logement/Logement";
import { Marche } from "./screens/Marche/Marche";
import { NouvelleRun } from "./screens/NouvelleRun/NouvelleRun";
import { NouvelleVideo } from "./screens/NouvelleVideo/NouvelleVideo";
import { Palmares } from "./screens/Palmares/Palmares";
import { Reglages } from "./screens/Reglages/Reglages";
import { Souffler } from "./screens/Souffler/Souffler";
import { useRunStore } from "./stores/runStore";

/**
 * Routes en français (SPEC-UI §1.7). **Ce routeur ne voit que le jeu** : la porte d'entrée
 * (choix bac à sable / compte, inscription, connexion) est montée *avant* lui par `App.tsx`,
 * sur l'état de `sessionStore`. Elle n'est donc pas une route — on ne peut pas y arriver par
 * une URL, et on ne peut pas la manquer : tant que la question n'est pas répondue, le jeu
 * n'existe pas.
 *
 * ⟦A-7⟧ **La chambre est l'accueil**, et les panneaux s'ouvrent PAR-DESSUS : Bibliothèque,
 * Arbre, Journal et Nouvelle vidéo sont donc des routes **filles** de `Studio`, rendues dans
 * son `<Outlet/>`. L'Atelier, lui, est plein écran et sort de la chambre — « on n'est plus
 * dans la chambre, on travaille » (décision 4).
 */
export function Routeur() {
  const run = useRunStore((s) => s.run);

  if (!run) {
    return (
      <Routes>
        <Route path="/nouvelle-run" element={<NouvelleRun />} />
        {/* ⟦PLAN §4.11⟧ Le palmarès vit aussi ICI, et c'est le cas qui compte le plus : un
            compte dont la dernière chaîne est morte n'a **aucune** run active, donc toute route
            retombait sur « lance ta chaîne ». Les mémoriaux existaient en base, et le seul
            moment où on veut les relire était le seul où ils étaient injoignables. L'écran ne lit
            jamais la run courante — il n'a pas besoin qu'il y en ait une. */}
        <Route path="/palmares" element={<Palmares />} />
        {/* ⟦A-97⟧ L'historique suit le palmarès dans LES TROIS jeux de routes, et pour la même
            raison — sauf qu'ici elle est encore plus littérale : c'est l'écran qu'on ouvre
            juste APRÈS avoir abandonné. `runStore.abandonner()` remet `run` à `null`, donc le
            routeur bascule sur ce jeu-ci dans la seconde qui suit ; monté seulement plus bas,
            l'écran se déroberait sous les pieds du joueur au moment précis où il vient d'agir
            dessus. Il ne lit jamais la run courante — il n'a pas besoin qu'il y en ait une. */}
        <Route path="/historique" element={<Historique />} />
        <Route path="*" element={<Navigate to="/nouvelle-run" replace />} />
      </Routes>
    );
  }

  // Une run terminée ne se joue plus (`run_not_active`) : on ne laisse pas le joueur cliquer
  // dans le vide, on l'emmène à l'écran de fin.
  //
  // ⚠️ **`ended` est le quatrième statut, et il manquait.** C'est celui que pose `clore_run`
  // après le million (PLAN §4.13) : une run refermée sur sa victoire, pas une défaite. Sans
  // cette ligne, clore sa run ramenait dans une chambre qui refusait toutes les actions.
  if (run.state.status === "game_over" || run.state.status === "ended") {
    return (
      <Routes>
        {/* ⟦PLAN §4.11⟧ « La run n'est jamais supprimée : elle devient un mémorial
            **consultable**. » C'est ici, sur l'écran de fin, que ça compte le plus : le joueur
            qui vient de perdre est celui qui veut relire ce que sa chaîne a été. Le palmarès
            est donc joignable des DEUX côtés — sinon, la seule route atteignable en fin de
            partie étant `Fin`, la promesse tenait pour tout le monde sauf pour les morts. */}
        <Route path="/palmares" element={<Palmares />} />
        {/* ⟦A-97⟧ Deuxième des trois. Le joueur qui vient de perdre est celui qui veut relire
            ce que ses chaînes ont été — et c'est le seul état où le catch-all mène à `Fin`,
            donc le seul où une route oubliée est strictement injoignable. */}
        <Route path="/historique" element={<Historique />} />
        <Route path="*" element={<Fin />} />
      </Routes>
    );
  }

  // ⟦PLAN §4.13⟧ **Le million.** La victoire ne pose PAS `game_over` — elle pose
  // `victory_locked` puis `status = "won"` —, si bien que l'écran de fin, l'objectif de toute
  // la partie, était strictement inatteignable : le joueur voyait la pop-up de palier et
  // restait dans sa chambre. On route donc sur l'état, pas sur l'événement du tour : tant que
  // le joueur n'a pas choisi entre clore et continuer, c'est la seule chose à l'écran, et un
  // F5 ne peut pas lui faire manquer la question.
  if (run.state.status === "won" && !run.state.flags.apres_la_rue) {
    return (
      <Routes>
        <Route path="*" element={<Victoire />} />
      </Routes>
    );
  }

  // ⟦C60 · A-177⟧ Les interrupteurs du ménage : une route dont le système est coupé ne mène plus nulle part
  // (la même garde que pour une mécanique pas encore ouverte, ⟦A-5⟧) — l'écran et son code restent.
  const sys = interrupteurs();

  return (
    <Routes>
      <Route element={<Studio />}>
        <Route path="/" element={null} />
        <Route path="/nouvelle-video" element={<NouvelleVideo />} />
        <Route path="/journal" element={<Journal />} />
        <Route path="/reglages" element={<Reglages />} />
        {/* Les remèdes de santé mentale (`pause`, `therapie`, `annoncer_pause` ⟦A-2⟧) : codés
            dans le moteur depuis M1, sans écran jusqu'ici. Toujours joignable — on ne verrouille
            pas la porte de sortie d'un game over. */}
        <Route path="/souffler" element={<Souffler />} />
        {/* Le palmarès : les chaînes passées et leur mémorial, le classement. Pas de garde
            d'escalier — ce n'est pas une mécanique de jeu, c'est une consultation ; l'écran dit
            lui-même pourquoi il est vide en bac à sable. */}
        <Route path="/palmares" element={<Palmares />} />
        {/* ⟦A-97⟧ Troisième des trois : l'historique en cours de partie, panneau posé sur la
            chambre comme le palmarès. Pas de garde d'escalier — c'est une consultation, et
            l'écran dit lui-même pourquoi il est vide en bac à sable. */}
        <Route path="/historique" element={<Historique />} />
        {/* ⟦A-5⟧ Une mécanique pas encore ouverte n'a pas d'écran : on rentre à la chambre. */}
        <Route
          path="/arbre"
          element={
            <Garde ouverte={ouvert(run.state, "arbre")}>
              <Arbre />
            </Garde>
          }
        />
        <Route
          path="/bibliotheque"
          element={
            <Garde ouverte={sys.bibliotheque && ouvert(run.state, "longue")}>
              <Bibliotheque />
            </Garde>
          }
        />
        {/* ⟦A-44 · A-71⟧ Le guide MeTube. Gardé par la CAPACITÉ, pas par un palier : le nœud
            porte déjà sa porte d'abonnés (`abonnes_min: 3000`), et la redoubler ici ouvrirait
            un deuxième endroit où se tromper. Éteint (`meta.guide_actif`), le nœud n'est pas
            dans l'arbre, la capacité est inaccessible, et la route retombe à la chambre. */}
        <Route
          path="/guide"
          element={
            <Garde ouverte={capacite(run.state, "guide_metube")}>
              <Guide />
            </Garde>
          }
        />
        {/* ⟦A-11 §4⟧ La boutique et le logement n'existent qu'après le Grand Saut : avant, il
            n'y a ni Clicks à sortir ni loyer à fuir. Même garde que la boîte à Clicks. */}
        <Route
          path="/boutique"
          element={
            <Garde ouverte={sys.boutique && ouvert(run.state, "argent")}>
              <Boutique />
            </Garde>
          }
        />
        <Route
          path="/logement"
          element={
            <Garde ouverte={sys.logement && ouvert(run.state, "argent")}>
              <Logement />
            </Garde>
          }
        />
        {/* Les vidéos jalons (§9.3, PLAN §4.13) : le **seul levier exponentiel** du jeu, et
            désormais jouable depuis cet écran — il ouvre l'Atelier en mode jalon. Pas de garde
            d'escalier : ce sont leurs propres conditions de contenu (palier, nœuds, flags,
            image) qui décident, et c'est le moteur qui les tient. */}
        {/* ⟦C60 · A-177⟧ Les jalons sont coupés : la route ramène à la chambre, comme une mécanique fermée. */}
        <Route
          path="/jalons"
          element={
            <Garde ouverte={sys.jalons}>
              <Jalons />
            </Garde>
          }
        />
        {/* ⟦SPEC-PRODUCTEUR §11.6 · A-66⟧ L'équipe — l'écran de la délégation. **Aucune garde
            de route** : l'écran se garde lui-même sur `meta.equipe_active` (l'interrupteur) et
            sait dire « personne ne travaille pour toi » quand le nœud n'est pas acheté. Une
            garde ici en ferait une deuxième, et deux gardes qui ne s'accordent pas, c'est le
            désaccord silencieux que l'audit des maillons a nommé. */}
        <Route path="/equipe" element={<Equipe />} />
        {/* ⟦A-4 E · M3.30⟧ Le marché des freelances — le maillon 3 de l'étage 1, rompu depuis
            M2.10. Même règle que L'équipe : aucune garde de route, l'écran se garde lui-même
            (il sait dire « le marché s'ouvre par l'arbre ») — deux gardes qui ne s'accordent
            pas, c'est le désaccord silencieux que l'audit des maillons a nommé. */}
        <Route path="/marche" element={<Marche />} />
        {/* ⟦SPEC-FIL §9.3 · M3.23⟧ Le Fil. Même garde d'escalier que le bouton qui l'ouvre —
            `lire_commentaires` s'ouvre à la 3ᵉ publication ⟦A-5⟧ —, mais **aucun étage neuf** :
            le Fil se découvre à l'arrivée de son premier commentaire, pas à un palier. */}
        <Route
          path="/fil"
          element={
            <Garde ouverte={ouvert(run.state, "commentaires")}>
              <Fil />
            </Garde>
          }
        />
      </Route>

      {/* Plein écran, hors chambre. */}
      <Route path="/atelier" element={<Atelier />} />
      <Route path="/fin" element={<Fin />} />

      <Route path="/nouvelle-run" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function Garde({ ouverte, children }: { ouverte: boolean; children: ReactElement }) {
  return ouverte ? children : <Navigate to="/" replace />;
}
